import { gql, useQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
} from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import { TransferState } from '@interfaces/transfer';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { Selector } from '@ovComponents/2-component/selector/selector';
import { FilterModal } from '@ovComponents/3-pattern/filterModal/filterModal';
import {
  type ActiveOrganizationProps,
  UserContext,
  usePermissions,
} from '@providers/userContextProvider';
import { STATE_CHANGES_FIELD } from '@utils/reusableGraphQLFields';
import { usePageState } from '@utils/usePageState';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryBuilder } from '../utils';
import { PendingTransactionsBar } from './pendingTransactionsBar';
import { PendingTransactionTable } from './pendingTransactionTable';

export type IPendingTransactionsOptions = {
  customTitle: TranslatedString;
  enableFilter?: boolean;
  filterForCanceled?: boolean;
  filterForFailed?: boolean;
  filterForInitiated?: boolean;
  filterForProcessing?: boolean;
  filterForReady?: boolean;
  filterForReconciled?: boolean;
  filterForRequested?: boolean;
  filterForReviewing?: boolean;
  display: 'table' | 'bar';
  segmentBy: string;
  enableDirectionView: boolean;
  enableStateView: boolean;
};

export const FETCH_TRANSFERS_QUERY = (
  permissions: string[],
  showTableData: boolean,
) => `query fetchTransfers($input: FetchTransfersInput!) {
  fetchTransfers(input: $input) {
    transfers {
      id
      amountCents
      state
      type
      ${
        showTableData
          ? `
        initiatedByRepresentative
        source
        sourceId
        ${STATE_CHANGES_FIELD}
        instantInvest
        dividendFor {
          ticker
        }
        fromSubAccount {
          id
          account {
            type
          }
        }
        failReason
        cancelReason
        cancelReconciledReason
        bankAccount {
          id
          name
          institutionNumber
          bankAccountNumber
          transitNumber
          ${permissions.includes('read:bank_account_disclosed') ? 'transitNumberDisclosed bankAccountNumberDisclosed' : ''}
        }
        isWithdrawAll
        ${permissions.includes('read:alert_report_basic') ? 'isSuspicious' : ''}
        ${
          permissions.includes('write:alert_report_basic')
            ? `
          alertReport {
            id
            rules {
              type
              translatedDescription { ${TRANSLATED_STRING_FIELDS} }
            }
          }
        `
            : ''
        }
        subAccount {
          id
          account {
            id
            type
            user {
              id
              type
              entityName
              ${permissions.includes('read:client_low_risk_pii') ? 'firstName middleName lastName' : ''}
            }
            organization {
              id
            }
          }
          goal {
            name
          }
          financialProduct {
            translatedName { ${TRANSLATED_STRING_FIELDS} }
          }
          statistics {
            marketValueCents
            moneyAvailableCents
            availableFundsToWithdrawCents
          }
        }
        createdAt
        reconciledAt
        isRecurring
        manualProcess {
          id
          type
          signedUrl
          institution { name }
          transferAccount
          transferAccountNumber
          transferMethod
        }
      `
          : ''
      }

    }
    totalCount
  }
}`;

export const FETCH_TRANSFERS = (
  permissions: string[],
  showTableData: boolean,
) => gql`${FETCH_TRANSFERS_QUERY(permissions, showTableData)}`;

export const PendingTransactions = ({
  options,
}: {
  options: IPendingTransactionsOptions;
}) => {
  const { t } = useTranslation(['components', 'shared', 'transfer']);
  const { permissions } = usePermissions();
  const { activeOrganization } = useContext(UserContext);
  const [source, setSource] = usePageState('any', 'tfr/source');
  const [type, setType] = usePageState('any', 'tfr/type');
  const [states, setStates] = useState<TransferState[]>(
    Object.values(TransferState),
  );
  const [statesFilter, setStatesFilter] = useState<TransferState[]>(
    Object.values(TransferState),
  );
  const [view, setView] = useState<'type' | 'state'>(
    options.enableStateView ? 'state' : 'type',
  );
  const navigate = useNavigate();

  const MAP_STATE_FILTER: Record<TransferState, boolean> = {
    [TransferState.Canceled]: !!options.filterForCanceled,
    [TransferState.Failed]: !!options.filterForFailed,
    [TransferState.Initiated]: !!options.filterForInitiated,
    [TransferState.Processing]: !!options.filterForProcessing,
    [TransferState.Ready]: !!options.filterForReady,
    [TransferState.Reconciled]: !!options.filterForReconciled,
    [TransferState.Requested]: !!options.filterForRequested,
    [TransferState.Reviewing]: !!options.filterForReviewing,
  };

  useEffect(() => {
    if (options.enableFilter) {
      const newStatesFilter = Object.entries(MAP_STATE_FILTER)
        .filter(([_, value]) => value)
        .map(([key]) => key as TransferState);
      setStatesFilter(newStatesFilter);
      setStates(newStatesFilter);
    }
  }, [options]);

  useEffect(() => {
    if (options.enableStateView && !options.enableDirectionView) {
      setView('state');
    } else {
      setView('type');
    }
  }, [options.enableStateView, options.enableDirectionView]);

  const getQuery = (
    field: 'type' | 'state',
    activeOrganization: ActiveOrganizationProps,
    states: TransferState[],
  ) => ({
    filters: [
      {
        field: 'organization',
        comparison: 'EQUALS',
        value: activeOrganization.id,
      },
      {
        field: 'state',
        comparison: 'IN',
        value: states.join(', '),
      },
    ],
    type: 'TRANSFER',
    grouping: {
      valueField: 'amountCents',
      field,
      valueAggregation: 'SUM',
      type: 'GROUPING',
    },
  });

  const generateSourceFilter = (filteredSource: string): any => {
    if (filteredSource === 'any') return { source: undefined };
    if (filteredSource === 'USER') {
      return {
        source: 'USER',
        initiatedByRepresentative: false,
      };
    }
    if (filteredSource === 'REPRESENTATIVE') {
      return {
        source: 'USER',
        initiatedByRepresentative: true,
      };
    }
    return { source: filteredSource };
  };

  const organizationId = activeOrganization.id;

  const queryFilter = {
    ...generateSourceFilter(source),
    type: type === 'any' ? undefined : type,
    states: states.length === 0 ? undefined : states,
    organizationId: organizationId ?? activeOrganization.id,
  };

  const {
    loading: tableLoading,
    data: tableData,
    previousData: tablePreviousData,
    refetch,
  } = useQuery(FETCH_TRANSFERS(permissions, options.display === 'table'), {
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
    skip: options.display !== 'table',
    variables: {
      input: {
        filter: {
          ...queryFilter,
        },
        pagination: {
          sortField: 'createdAt',
          sortDesc: false,
          perPage: 5,
          offSet: 0,
        },
      },
    },
  });

  const { data: barData, loading: barLoading } = useQueryBuilder(
    getQuery(view, activeOrganization, states),
  );

  useEffect(
    () =>
      function cleanupOnUnmount() {
        setSource('any');
        setType('any');
        setStates(Object.values(TransferState));
      },
    [],
  );

  return (
    <Card loading={tableLoading || barLoading}>
      <CardContent
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='titleLarge'>
          {translateBackend(options.customTitle)}
        </Typography>
        <Box display='flex' alignItems='center' gap={0.5}>
          {options.enableFilter && (
            <FilterModal filterExists={states.length > 0}>
              <SelectField
                fullWidth
                multiple
                label={t('accountsDetail:state')}
                value={states}
                onChange={(e: any) => setStates(e.target.value)}
              >
                {statesFilter.map((state) => (
                  <MenuItem key={state} value={state}>
                    {t(`transfer:states.${state}`)}
                  </MenuItem>
                ))}
              </SelectField>
            </FilterModal>
          )}
          {options.enableDirectionView &&
            options.enableStateView &&
            options.display === 'bar' && (
              <Selector
                testId='pending-transactions-bar-item-view'
                sx={{ marginLeft: 3 }}
                onChange={(e: any) =>
                  setView(e.target.value as 'type' | 'state')
                }
                selectedOptionType={view}
                options={[
                  ...(options.enableDirectionView
                    ? [
                        {
                          label: t(
                            'transfer:pendingTransactionsBar.byDirection',
                          ),
                          type: 'type',
                          dataTestId: 'pending-transactions-bar-item-type',
                        },
                      ]
                    : []),
                  ...(options.enableStateView
                    ? [
                        {
                          label: t('transfer:pendingTransactionsBar.byState'),
                          type: 'state',
                          dataTestId: 'pending-transactions-bar-item-state',
                        },
                      ]
                    : []),
                ]}
              />
            )}
          <Button
            label={t('shared:viewAll')}
            variant='text'
            onClick={() => {
              navigate('/transactions');
            }}
          />
        </Box>
      </CardContent>
      {options.display === 'table' ? (
        <PendingTransactionTable
          transfers={
            (tableData || tablePreviousData)?.fetchTransfers?.transfers ?? []
          }
          refetch={refetch}
        />
      ) : (
        <PendingTransactionsBar
          data={barData?.generateReport?.grouping ?? []}
          view={view}
          loading={barLoading}
        />
      )}
    </Card>
  );
};
